import { useState, useCallback } from 'react';

/**
 * Custom hook för att hantera dynamiska anteckningar
 * 
 * @param {Function} setHasUnsavedNotes - Callback för att markera osparade ändringar
 * @returns {Object} - State och funktioner för dynamiska anteckningar
 */
const useDynamicNotes = (setHasUnsavedNotes) => {
  const [dynamicNotes, setDynamicNotes] = useState([]);
  const [badgeCounts, setBadgeCounts] = useState({});
  const [focusNoteId, setFocusNoteId] = useState(null);

  /**
   * Lägg till ny anteckning
   */
  const addNewNote = useCallback((predefinedTitle = null) => {
    const id = Date.now() + Math.random(); // Unik ID
    let title = predefinedTitle;
    
    if (predefinedTitle) {
      // Hantera badge-titlar med räknare
      const currentCount = badgeCounts[predefinedTitle] || 0;
      const newCount = currentCount + 1;
      setBadgeCounts(prev => ({
        ...prev,
        [predefinedTitle]: newCount
      }));
      
      if (newCount > 1) {
        title = `${predefinedTitle} ${newCount}`;
      }
    }
    
    const newNote = {
      id,
      title: title || '',
      content: '',
      isEditing: !predefinedTitle // Om det inte är en badge, börja i edit-läge för titel
    };
    
    setDynamicNotes(prev => [...prev, newNote]);
    
    // Dynamiska anteckningar räknas som anteckningar
    setHasUnsavedNotes(true);
    
    // Sätt fokus på den nya anteckningen
    setFocusNoteId(id);
    
    return id;
  }, [badgeCounts, setHasUnsavedNotes]);

  /**
   * Uppdatera titel på anteckning
   */
  const updateNoteTitle = useCallback((id, title) => {
    setDynamicNotes(prev => prev.map(note => 
      note.id === id ? { ...note, title, isEditing: false } : note
    ));
    
    // Dynamiska anteckningar räknas som anteckningar
    setHasUnsavedNotes(true);
  }, [setHasUnsavedNotes]);

  /**
   * Uppdatera innehåll på anteckning
   */
  const updateNoteContent = useCallback((id, content) => {
    setDynamicNotes(prev => prev.map(note => 
      note.id === id ? { ...note, content } : note
    ));
    // För draft-estimat: markera som osparade ändringar vid onBlur
    // För skickade estimat: spara separat vid onBlur
  }, []);

  /**
   * Ta bort anteckning
   */
  const deleteNote = useCallback((id) => {
    setDynamicNotes(prev => prev.filter(note => note.id !== id));
    
    // Dynamiska anteckningar räknas som anteckningar
    setHasUnsavedNotes(true);
  }, [setHasUnsavedNotes]);

  /**
   * Toggla edit-läge för titel
   */
  const toggleTitleEdit = useCallback((id) => {
    setDynamicNotes(prev => prev.map(note => 
      note.id === id ? { ...note, isEditing: !note.isEditing } : note
    ));
  }, []);

  /**
   * Rensa fokus på anteckning
   */
  const clearFocusNoteId = useCallback(() => {
    setFocusNoteId(null);
  }, []);

  return {
    // State
    dynamicNotes,
    badgeCounts,
    focusNoteId,
    
    // Setters (för initial data loading)
    setDynamicNotes,
    setBadgeCounts,
    setFocusNoteId,
    
    // Actions
    addNewNote,
    updateNoteTitle,
    updateNoteContent,
    deleteNote,
    toggleTitleEdit,
    clearFocusNoteId,
  };
};

export default useDynamicNotes;
